import {
  isGroupHeaders,
  isGroupedPickups,
  isSingleInGroupPickups
} from './utils';

/**
 * Validates if a node has the required data structure for processing
 * @param {Object} node - AG Grid node object
 * @returns {boolean} - True if node is valid for processing
 */
const isValidNode = (node) => {
  if (!node?.data) return false;
  
  const data = node.data;
  const isPickupRow = isGroupedPickups(data) || isSingleInGroupPickups(data);
  const isGroupRow = isGroupHeaders(data);
  
  if (isPickupRow) {
    return !!data.pickupId;
  }
  if (isGroupRow) {
    return !!data.id;
  }
  
  return false;
};

/**
 * Maintains group integrity after AG Grid sorting by ensuring:
 * - Group headers are immediately followed by their children (in sorted order)
 * - Standalone pickups (single-in-group) are only included if not part of a group
 * - Grouped pickups are placed directly after their parent group header
 * 
 * Optimized implementation using Maps for O(1) lookups while preserving sort order.
 * 
 * @param {Array<Object>} sortedNodes - Array of AG Grid nodes after sorting
 * @returns {Array<Object>} - Reordered nodes with maintained group integrity
 */
const maintainGroupIntegrity = (sortedNodes) => {
  if (!sortedNodes?.length) return [];

  // Build lookup maps for O(1) access while preserving order
  const groupedPickupsByGroup = new Map(); // groupId -> array of grouped pickup nodes (in sorted order)
  const hasGroupHeaderSet = new Set(); // Set of groupIds that have headers
  
  // First pass: categorize all nodes and build lookup structures
  sortedNodes.forEach((node) => {
    const data = node.data;
    
    if (isGroupHeaders(data)) {
      hasGroupHeaderSet.add(data.id);
      // Initialize grouped pickups array for this group
      if (!groupedPickupsByGroup.has(data.id)) {
        groupedPickupsByGroup.set(data.id, []);
      }
    } else if (isGroupedPickups(data)) {
      const groupId = data.groupId;
      if (!groupedPickupsByGroup.has(groupId)) {
        groupedPickupsByGroup.set(groupId, []);
      }
      // Add children in the order they appear in sortedNodes (preserves sort order)
      groupedPickupsByGroup.get(groupId).push(node);
    }
  });
  
  // Second pass: build result array maintaining group integrity and sort order
  const result = [];
  const processedGroupIds = new Set();
  const processedStandalonePickups = new Set();
  
  sortedNodes.forEach((node) => {
    const data = node.data;
    
    if (isGroupHeaders(data)) {
      const groupId = data.id;
      
      // Only process each group header once (in the order it appears)
      if (!processedGroupIds.has(groupId)) {
        processedGroupIds.add(groupId);
        
        // Add group header
        result.push(node);
        
        // Add all grouped pickups for this group (already in sorted order)
        const groupChildren = groupedPickupsByGroup.get(groupId) || [];
        result.push(...groupChildren);
      }
    } else {
      // Only add standalone pickups that aren't children of any group
      const groupId = data.groupId;
      const hasGroupHeader = hasGroupHeaderSet.has(groupId);
      
      if (!hasGroupHeader && !processedStandalonePickups.has(data.pickupId)) {
        processedStandalonePickups.add(data.pickupId);
        result.push(node);
      }
    }
    // Note: groupedPickups are already added when processing their group headers
  });
  
  return result;
};

/**
 * Post-sort handler for AG Grid that maintains group integrity after sorting.
 * This ensures group headers are always followed by their children, maintaining
 * the hierarchical structure required for grouped pickup display.
 * 
 * Only executes when there is an active sort applied to the grid.
 * 
 * @param {Object} params - AG Grid postSortRows callback parameters
 * @param {Array<Object>} params.nodes - Array of AG Grid nodes after sorting
 * @param {Object} params.api - AG Grid API instance
 */
const postSortGroupedPickupsRows = (params) => {
  if (!params?.nodes?.length) return;

  // Filter valid nodes first
  const validNodes = params.nodes.filter(isValidNode);

  // Apply group integrity after AG Grid's sorting
  const groupedNodes = maintainGroupIntegrity(validNodes);
  
  // Replace nodes array with reordered nodes
  params.nodes.splice(0, params.nodes.length, ...groupedNodes);
};

export {
  postSortGroupedPickupsRows,
  maintainGroupIntegrity,
  isValidNode
};

